import { useState, useEffect } from "react";
import { MainLayout } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Users,
  Search,
  Download,
  Eye,
  Key,
  Trash2,
  Loader2,
  Calendar,
  Clock,
  Mail,
  Phone,
  User,
  RefreshCw,
  UserCheck,
  Building,
  MapPin,
  FileText,
  Plus,
  Edit,
  UserPlus,
  CheckCircle,
  XCircle,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

// Mapping stati con colori
const STATI_LEAD = {
  nuovo: { label: "Nuovo", color: "bg-blue-100 text-blue-800" },
  contattato: { label: "Contattato", color: "bg-yellow-100 text-yellow-800" },
  interessato: { label: "Interessato", color: "bg-green-100 text-green-800" },
  convertito: { label: "Convertito", color: "bg-purple-100 text-purple-800" },
  perso: { label: "Perso", color: "bg-red-100 text-red-800" },
};

export default function AdminPotenzialiClientiPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStato, setFilterStato] = useState("all");
  
  // Dialog states
  const [selectedLead, setSelectedLead] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Form states
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  
  // New lead form
  const [newLead, setNewLead] = useState({
    nome: "",
    email: "",
    telefono: "",
    azienda: "",
    note: "",
    fonte: "manuale",
  });

  useEffect(() => {
    loadLeads();
  }, [filterStato]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const statoParam = filterStato !== "all" ? `&stato=${filterStato}` : "";
      const response = await fetch(`${API_URL}/api/admin/leads?limit=200${statoParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
        setTotal(data.total || 0);
        setStats(data.stats || {});
      } else {
        toast.error("Errore nel caricamento dei dati");
      }
    } catch (error) {
      console.error("Errore:", error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async () => {
    if (!newLead.nome || !newLead.email) {
      toast.error("Nome e email sono obbligatori");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/admin/leads`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newLead),
      });

      if (response.ok) {
        toast.success("Lead creato con successo");
        setShowAddDialog(false);
        setNewLead({ nome: "", email: "", telefono: "", azienda: "", note: "", fonte: "manuale" });
        loadLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/leads/${selectedLead.lead_id}/password`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ new_password: newPassword }),
        }
      );

      if (response.ok) {
        toast.success("Password aggiornata con successo");
        setShowPasswordDialog(false);
        setNewPassword("");
        setSelectedLead(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateLead = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/leads/${selectedLead.lead_id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            nome: selectedLead.nome,
            email: selectedLead.email,
            telefono: selectedLead.telefono,
            azienda: selectedLead.azienda,
            note: selectedLead.note,
            stato: selectedLead.stato,
            fonte: selectedLead.fonte,
          }),
        }
      );

      if (response.ok) {
        toast.success("Lead aggiornato con successo");
        setShowEditDialog(false);
        loadLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/leads/${selectedLead.lead_id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success("Lead eliminato");
        setShowDeleteDialog(false);
        setSelectedLead(null);
        loadLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  const handleConvertLead = async (lead) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/leads/${lead.lead_id}/converti`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        if (data.temp_password) {
          toast.info(`Password temporanea: ${data.temp_password}`, { duration: 10000 });
        }
        loadLeads();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella conversione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/admin/leads/export/csv`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "leads_potenziali.csv";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("Export completato");
      } else {
        toast.error("Errore nell'export");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const filteredLeads = leads.filter(
    (lead) =>
      lead.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.telefono?.includes(searchTerm) ||
      lead.azienda?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-leads-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-purple-600" />
              Potenziali Clienti (Leads)
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestione lead commerciali - Database separato dagli utenti
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={loadLeads} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Aggiorna
            </Button>
            <Button onClick={() => setShowAddDialog(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-lead-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Lead
            </Button>
            <Button onClick={exportCSV} className="bg-green-600 hover:bg-green-700" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              Esporta CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilterStato("all")}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xl font-bold">{total}</p>
                  <p className="text-xs text-muted-foreground">Totale</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {Object.entries(STATI_LEAD).map(([key, { label, color }]) => (
            <Card 
              key={key} 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filterStato === key ? 'ring-2 ring-purple-500' : ''}`}
              onClick={() => setFilterStato(filterStato === key ? "all" : key)}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <Badge className={color}>{stats[key] || 0}</Badge>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search & Filter */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per nome, email, telefono o azienda..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={filterStato} onValueChange={setFilterStato}>
                <SelectTrigger className="w-[180px]" data-testid="filter-stato">
                  <SelectValue placeholder="Filtra per stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  {Object.entries(STATI_LEAD).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Elenco Lead ({filteredLeads.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessun lead trovato</p>
                <Button variant="link" onClick={() => setShowAddDialog(true)}>
                  Aggiungi il primo lead
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Contatti</TableHead>
                      <TableHead>Azienda</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Fonte</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.map((lead) => (
                      <TableRow key={lead.lead_id} data-testid={`lead-row-${lead.lead_id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-purple-600" />
                            </div>
                            {lead.nome || "-"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="w-3 h-3 text-muted-foreground" />
                              {lead.email}
                            </div>
                            {lead.telefono && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                {lead.telefono}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {lead.azienda ? (
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-muted-foreground" />
                              {lead.azienda}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATI_LEAD[lead.stato]?.color || "bg-gray-100"}>
                            {STATI_LEAD[lead.stato]?.label || lead.stato}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground capitalize">
                            {lead.fonte?.replace("_", " ") || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{lead.data_inserimento || "-"}</div>
                            <div className="text-muted-foreground text-xs">{lead.ora_inserimento || ""}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedLead(lead);
                                setShowDetailDialog(true);
                              }}
                              title="Dettagli"
                              data-testid={`view-btn-${lead.lead_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedLead({...lead});
                                setShowEditDialog(true);
                              }}
                              title="Modifica"
                              data-testid={`edit-btn-${lead.lead_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedLead(lead);
                                setShowPasswordDialog(true);
                              }}
                              title="Modifica Password"
                              data-testid={`password-btn-${lead.lead_id}`}
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            {lead.stato !== "convertito" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleConvertLead(lead)}
                                title="Converti in Utente"
                                data-testid={`convert-btn-${lead.lead_id}`}
                              >
                                <UserPlus className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => {
                                setSelectedLead(lead);
                                setShowDeleteDialog(true);
                              }}
                              title="Elimina"
                              data-testid={`delete-btn-${lead.lead_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Lead Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Nuovo Lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Nome *</label>
                <Input
                  value={newLead.nome}
                  onChange={(e) => setNewLead({...newLead, nome: e.target.value})}
                  placeholder="Nome completo"
                  data-testid="new-lead-nome"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                  placeholder="email@esempio.com"
                  data-testid="new-lead-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Telefono</label>
                  <Input
                    value={newLead.telefono}
                    onChange={(e) => setNewLead({...newLead, telefono: e.target.value})}
                    placeholder="+39 333..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Fonte</label>
                  <Select value={newLead.fonte} onValueChange={(v) => setNewLead({...newLead, fonte: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manuale">Manuale</SelectItem>
                      <SelectItem value="sito">Sito Web</SelectItem>
                      <SelectItem value="fiera">Fiera</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Azienda/Centro</label>
                <Input
                  value={newLead.azienda}
                  onChange={(e) => setNewLead({...newLead, azienda: e.target.value})}
                  placeholder="Nome centro estetico"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Note</label>
                <Textarea
                  value={newLead.note}
                  onChange={(e) => setNewLead({...newLead, note: e.target.value})}
                  placeholder="Note aggiuntive..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleAddLead} disabled={saving} data-testid="save-new-lead-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Crea Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Lead Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Modifica Lead</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Nome</label>
                  <Input
                    value={selectedLead.nome || ""}
                    onChange={(e) => setSelectedLead({...selectedLead, nome: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={selectedLead.email || ""}
                    onChange={(e) => setSelectedLead({...selectedLead, email: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Telefono</label>
                    <Input
                      value={selectedLead.telefono || ""}
                      onChange={(e) => setSelectedLead({...selectedLead, telefono: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Stato</label>
                    <Select 
                      value={selectedLead.stato || "nuovo"} 
                      onValueChange={(v) => setSelectedLead({...selectedLead, stato: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATI_LEAD).map(([key, { label }]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Azienda/Centro</label>
                  <Input
                    value={selectedLead.azienda || ""}
                    onChange={(e) => setSelectedLead({...selectedLead, azienda: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Note</label>
                  <Textarea
                    value={selectedLead.note || ""}
                    onChange={(e) => setSelectedLead({...selectedLead, note: e.target.value})}
                    rows={3}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleUpdateLead} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Salva Modifiche
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Dettagli Lead</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                    <User className="w-8 h-8 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xl font-semibold">{selectedLead.nome || "Nome non fornito"}</p>
                    <Badge className={STATI_LEAD[selectedLead.stato]?.color || "bg-gray-100"}>
                      {STATI_LEAD[selectedLead.stato]?.label || selectedLead.stato}
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </p>
                    <p className="font-medium">{selectedLead.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Telefono
                    </p>
                    <p className="font-medium">{selectedLead.telefono || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Building className="w-4 h-4" /> Azienda
                    </p>
                    <p className="font-medium">{selectedLead.azienda || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Città
                    </p>
                    <p className="font-medium">{selectedLead.citta || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Data Inserimento
                    </p>
                    <p className="font-medium">{selectedLead.data_inserimento || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Fonte</p>
                    <p className="font-medium capitalize">{selectedLead.fonte?.replace("_", " ") || "-"}</p>
                  </div>
                </div>

                {selectedLead.note && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4" /> Note
                    </p>
                    <p className="text-sm bg-slate-50 p-3 rounded-lg">{selectedLead.note}</p>
                  </div>
                )}

                {selectedLead.contatti_log && selectedLead.contatti_log.length > 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4" /> Storico Contatti ({selectedLead.contatti_log.length})
                    </p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedLead.contatti_log.map((log, idx) => (
                        <div key={idx} className="text-sm bg-slate-50 p-2 rounded">
                          <div className="flex justify-between text-muted-foreground text-xs">
                            <span className="capitalize">{log.tipo}</span>
                            <span>{new Date(log.data).toLocaleDateString('it-IT')}</span>
                          </div>
                          {log.nota && <p className="mt-1">{log.nota}</p>}
                          <p className="text-xs text-muted-foreground">di {log.operatore}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifica Password</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Modifica la password per <strong>{selectedLead.nome || selectedLead.email}</strong>
                </p>
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  Nota: La password sarà utilizzabile se il lead viene convertito in utente.
                </p>
                <div>
                  <label className="text-sm font-medium mb-2 block">Nuova Password</label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Inserisci la nuova password"
                    data-testid="new-password-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimo 6 caratteri</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleUpdatePassword} disabled={saving} data-testid="save-password-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                Salva Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Conferma Eliminazione</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
                  <XCircle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="font-medium">Stai per eliminare:</p>
                    <p className="text-sm text-muted-foreground">{selectedLead.nome || selectedLead.email}</p>
                  </div>
                </div>
                <p className="text-sm">
                  Questa azione non può essere annullata. Il lead verrà rimosso permanentemente dal database.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Annulla
              </Button>
              <Button variant="destructive" onClick={handleDeleteLead} disabled={saving} data-testid="confirm-delete-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Elimina Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
